// OLMPS v10 flat-glass primitives — the building blocks of the shell visual
// system. File keeps its historical name ("glass") because 60+ call sites
// import from it; exports and props are unchanged, the material is now the v10
// navy/ivory/gold flat glass (dark default, light via Settings).
//
// <Atmosphere/>      flat t.night page (no clouds / glows).
// <NeuSurface/>      core surface: t.glass fill + navyLine hairline. raised | inset.
// <GlassPanel/>      raised glass panel (name kept for call sites).
// <PrimaryButton/>   the ONE accent: solid gold pill, onGold label.
// <GlassPill/>       secondary chip: glass; active = gold.
// Check/Play/Calendar/Sparkle icons — stroke SVG line icons (no icon lib).

import React from 'react';
import { Pressable, StyleSheet, Text, View, ViewStyle, StyleProp } from 'react-native';
import Svg, { Rect, Circle, Path } from 'react-native-svg';
import { LinearGradient as Grad } from 'expo-linear-gradient';
import { useTheme, makeThemedStyles, radii } from '../v10/theme';

// ── Atmosphere ──────────────────────────────────────────────────────────────

/** Page field: vertical navy gradient + soft gold radiance at the top. Absolute-fills its parent. */
export function Atmosphere({ glow = true }: { glow?: boolean }) {
  const { t } = useTheme();
  return (
    <View pointerEvents="none" style={StyleSheet.absoluteFill}>
      <Grad colors={t.pageGradient} style={StyleSheet.absoluteFill} />
      {glow && (
        <Grad
          colors={[t.goldGlow.replace(/[\d.]+\)$/, '0.14)'), 'rgba(0,0,0,0)']}
          start={{ x: 0.5, y: 0 }}
          end={{ x: 0.5, y: 0.45 }}
          style={StyleSheet.absoluteFill}
        />
      )}
    </View>
  );
}

/** Depth fill for any hand-rolled panel: gradient glass + 1px inner top
 *  highlight. Place as the first child of an overflow-hidden, hairlined View. */
export function GlassFill({ strong = false, radius = radii.panel }: { strong?: boolean; radius?: number }) {
  const { t } = useTheme();
  return (
    <>
      <Grad
        colors={strong ? [t.navyRaised, t.navy] : t.panelGradient}
        start={{ x: 0.2, y: 0 }}
        end={{ x: 0.8, y: 1 }}
        style={StyleSheet.absoluteFill}
        pointerEvents="none"
      />
      <View
        pointerEvents="none"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 1,
          backgroundColor: t.panelHighlight,
          borderTopLeftRadius: radius,
          borderTopRightRadius: radius,
        }}
      />
    </>
  );
}

// ── NeuSurface ──────────────────────────────────────────────────────────────

export interface NeuSurfaceProps {
  children?: React.ReactNode;
  style?: StyleProp<ViewStyle>;
  radius?: number;
  /** raised (default) = extruded; inset = carved/pressed; flat = material only. */
  mode?: 'raised' | 'raisedSm' | 'raisedLg' | 'inset' | 'insetDeep' | 'flat';
  onPress?: () => void;
  disabled?: boolean;
}

/** Core flat-glass surface: t.glass fill + hairline; inset = darker well. */
export function NeuSurface({
  children,
  style,
  radius = radii.panel,
  mode = 'raised',
  onPress,
  disabled,
}: NeuSurfaceProps) {
  const { t, c, neu } = useTheme();
  const shadow = mode === 'flat' ? null : neu[mode];
  const isInset = mode.startsWith('inset');
  const s: StyleProp<ViewStyle> = [
    {
      backgroundColor: isInset ? c.baseDark : 'transparent',
      borderWidth: 1,
      borderColor: t.navyLine,
      borderRadius: radius,
      overflow: 'hidden',
    },
    shadow,
    style,
  ];
  if (onPress) {
    return (
      <Pressable
        onPress={onPress}
        disabled={disabled}
        style={({ pressed }) => [
          s,
          pressed && !isInset && { ...neu.inset, backgroundColor: c.baseDark },
        ]}
      >
        {!isInset && <GlassFill radius={radius} />}
        {children}
      </Pressable>
    );
  }
  return (
    <View style={s}>
      {!isInset && <GlassFill radius={radius} />}
      {children}
    </View>
  );
}

// ── GlassPanel (raised soft panel — name kept for legacy call sites) ────────

interface GlassPanelProps {
  children?: React.ReactNode;
  style?: StyleProp<ViewStyle>;
  /** Inner padding container style (gap, padding overrides…). */
  contentStyle?: StyleProp<ViewStyle>;
  radius?: number;
  /** Legacy blur prop — ignored; kept for call sites. */
  intensity?: number;
  /** Slightly lighter fill for text-dense panels. */
  strong?: boolean;
  onPress?: () => void;
  disabled?: boolean;
}

/** Flat glass panel: t.glass + navyLine hairline. `strong` uses t.glassStrong
 *  (modals, sheets). */
export function GlassPanel({
  children,
  style,
  contentStyle,
  radius = radii.panel,
  strong = false,
  onPress,
  disabled,
}: GlassPanelProps) {
  const { neu } = useTheme();
  const g = useStyles();
  const frame: StyleProp<ViewStyle> = [
    g.panelFrame,
    { borderRadius: radius, backgroundColor: 'transparent', overflow: 'hidden' },
    neu.raised,
    style,
  ];
  const inner = (
    <>
      <GlassFill strong={strong} radius={radius} />
      <View style={[g.panelContent, contentStyle]}>{children}</View>
    </>
  );
  if (onPress) {
    return (
      <Pressable onPress={onPress} disabled={disabled} style={frame}>
        {inner}
      </Pressable>
    );
  }
  return <View style={frame}>{inner}</View>;
}

// ── PrimaryButton ───────────────────────────────────────────────────────────

interface PrimaryButtonProps {
  label: string;
  onPress?: () => void;
  disabled?: boolean;
  icon?: React.ReactNode;
  small?: boolean;
  style?: StyleProp<ViewStyle>;
}

/** Gold hero button — solid t.gold, t.onGold label. */
export function PrimaryButton({ label, onPress, disabled, icon, small, style }: PrimaryButtonProps) {
  const { t, neu } = useTheme();
  const g = useStyles();
  const radius = radii.pill; // primary actions are full pills
  return (
    <Pressable
      onPress={onPress}
      disabled={disabled}
      style={({ pressed }) => [
        g.primaryFrame,
        { borderRadius: radius },
        small ? g.primarySmall : g.primaryLarge,
        neu.glowPrimary,
        pressed && g.primaryPressed,
        disabled && g.disabled,
        style,
      ]}
    >
      <Grad
        colors={[t.goldBright, t.gold]}
        start={{ x: 0.5, y: 0 }}
        end={{ x: 0.5, y: 1 }}
        style={[StyleSheet.absoluteFill, { borderRadius: radius }]}
        pointerEvents="none"
      />
      {icon}
      <Text style={[g.primaryLabel, small && g.primaryLabelSmall]}>{label}</Text>
    </Pressable>
  );
}

// ── GlassPill ───────────────────────────────────────────────────────────────

interface GlassPillProps {
  label: string;
  onPress?: () => void;
  disabled?: boolean;
  icon?: React.ReactNode;
  active?: boolean;
  style?: StyleProp<ViewStyle>;
}

/** Secondary chip — glass; active/pressed = solid gold. */
export function GlassPill({ label, onPress, disabled, icon, active, style }: GlassPillProps) {
  const g = useStyles();
  return (
    <Pressable
      onPress={onPress}
      disabled={disabled}
      style={({ pressed }) => [
        g.pillFrame,
        active || pressed ? g.pillActive : null,
        disabled && g.disabled,
        style,
      ]}
    >
      {({ pressed }) => (
        <>
          {icon}
          <Text style={[g.pillLabel, (active || pressed) && g.pillLabelActive]}>{label}</Text>
        </>
      )}
    </Pressable>
  );
}

// ── Line icons (stroke SVG) ─────────────────────────────────────────────────

type IconProps = { size?: number; color?: string };

export function CheckCircleIcon({ size = 14, color }: IconProps) {
  const { t } = useTheme();
  color = color ?? t.onGold;
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24">
      <Circle cx={12} cy={12} r={10} stroke={color} strokeWidth={2} fill="none" />
      <Path d="M8 12.5l2.6 2.6L16 9.5" stroke={color} strokeWidth={2} fill="none" strokeLinecap="round" strokeLinejoin="round" />
    </Svg>
  );
}

export function PlayCircleIcon({ size = 14, color }: IconProps) {
  const { t } = useTheme();
  color = color ?? t.goldBright;
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24">
      <Circle cx={12} cy={12} r={10} stroke={color} strokeWidth={2} fill="none" />
      <Path d="M10 8.5v7l5.5-3.5z" fill={color} />
    </Svg>
  );
}

export function CalendarIcon({ size = 14, color }: IconProps) {
  const { t } = useTheme();
  color = color ?? t.goldBright;
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24">
      <Rect x={3.5} y={5} width={17} height={15.5} rx={2.5} stroke={color} strokeWidth={2} fill="none" />
      <Path d="M3.5 9.5h17M8 2.8v4M16 2.8v4" stroke={color} strokeWidth={2} strokeLinecap="round" />
    </Svg>
  );
}

export function SparkleIcon({ size = 16, color }: IconProps) {
  const { t } = useTheme();
  color = color ?? t.onGold;
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24">
      <Path d="M12 3l1.9 5.6L19.5 10l-5.6 1.9L12 17.5l-1.9-5.6L4.5 10l5.6-1.4z" fill={color} />
      <Path d="M18.5 15.5l.9 2.6 2.6.9-2.6.9-.9 2.6-.9-2.6-2.6-.9 2.6-.9z" fill={color} opacity={0.85} />
    </Svg>
  );
}

// ── styles ──────────────────────────────────────────────────────────────────

const useStyles = makeThemedStyles(({ t, neu }) =>
  StyleSheet.create({
    panelFrame: {
      borderWidth: 1,
      borderColor: t.navyLine,
    },
    panelContent: { padding: 16, gap: 10 },

    primaryFrame: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 8,
      backgroundColor: t.gold,
      overflow: 'hidden',
    },
    primaryLarge: { paddingVertical: 14, paddingHorizontal: 22 },
    primarySmall: { paddingVertical: 8, paddingHorizontal: 16 },
    primaryPressed: { boxShadow: 'none', transform: [{ scale: 0.98 }], backgroundColor: t.goldDeep },
    primaryLabel: { color: t.onGold, fontWeight: '700', fontSize: 14, letterSpacing: 0.2 },
    primaryLabelSmall: { fontSize: 12, fontWeight: '800' },

    pillFrame: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 6,
      backgroundColor: t.glass,
      borderWidth: 1,
      borderColor: t.navyLine,
      borderRadius: radii.pill,
      paddingVertical: 8,
      paddingHorizontal: 15,
      ...neu.raisedSm,
    },
    pillActive: { backgroundColor: t.gold, borderColor: t.gold, ...neu.glowPrimary },
    pillLabel: { color: t.ivorySoft, fontWeight: '700', fontSize: 12 },
    pillLabelActive: { color: t.onGold },

    disabled: { opacity: 0.55 },
  }),
);
